"use client";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { ChevronLeft, X } from "lucide-react";
import { forwardRef, type HTMLAttributes } from "react";

const EditDialog = Dialog;

const EditDialogTrigger = DialogTrigger;

const EditDialogClose = DialogClose;

const EditDialogContent = forwardRef<
  React.ElementRef<typeof DialogContent>,
  React.ComponentPropsWithoutRef<typeof DialogContent> & {
    isChildDialog?: boolean;
    noCloseButton?: boolean;
    noFooter?: boolean;
    wrapperClassName?: string;
    forceDark?: boolean;
    fullScreenOnMobile?: boolean;
    bottomSheetOnMobile?: boolean;
  }
>(
  (
    {
      className,
      children,
      isChildDialog,
      noCloseButton,
      noFooter,
      wrapperClassName,
      forceDark,
      fullScreenOnMobile,
      bottomSheetOnMobile,
      ...props
    },
    ref,
  ) => (
    <DialogContent
      ref={ref}
      className={cn(
        "p-0 gap-0 max-h-dvh h-dvh md:h-auto md:max-h-[75vh] overflow-hidden",
        fullScreenOnMobile &&
          "w-screen h-dvh max-w-none left-0 top-0 translate-x-0 translate-y-0 rounded-none md:w-full md:max-w-lg md:h-auto md:max-h-[75vh] md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg",
        bottomSheetOnMobile &&
          "!left-0 !right-0 !top-auto !bottom-0 !mx-0 !h-[88dvh] !max-w-none !translate-x-0 !translate-y-0 rounded-b-none rounded-t-2xl border-x-0 border-b-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full md:!left-1/2 md:!right-auto md:!top-1/2 md:!bottom-auto md:!h-auto md:!max-h-[75vh] md:!max-w-lg md:!-translate-x-1/2 md:!-translate-y-1/2 md:rounded-lg md:border",
        {
          "bg-black text-white border-white/15": forceDark,
        },
        className,
      )}
      closeButtonClassName="hidden"
      disableAnimation={bottomSheetOnMobile}
      {...props}
    >
      {!noCloseButton && (
        <div
          className={cn("absolute top-0 left-0 right-0 bg-background z-50", {
            "bg-black": forceDark,
          })}
        >
          <EditDialogClose className="p-6">
            {!isChildDialog && <X className="h-4 w-4" />}
            {isChildDialog && <ChevronLeft className="h-4 w-4" />}
            <span className="sr-only">Close</span>
          </EditDialogClose>
        </div>
      )}

      <div
        className={cn(
          "overflow-y-auto max-h-full md:max-h-[75vh] pt-16",
          {
            "pb-28": !noFooter,
          },
          wrapperClassName,
        )}
      >
        {children}
      </div>
    </DialogContent>
  ),
);

EditDialogContent.displayName = DialogContent.displayName;

function EditDialogHeader({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return <DialogHeader className={cn("px-6", className)} {...props} />;
}

EditDialogHeader.displayName = DialogHeader.displayName;

const EditDialogTitle = forwardRef<
  React.ElementRef<typeof DialogTitle>,
  React.ComponentPropsWithoutRef<typeof DialogTitle>
>(({ className, ...props }, ref) => {
  return (
    <DialogTitle
      ref={ref}
      className={cn("text-2xl text-left", className)}
      {...props}
    />
  );
});

EditDialogTitle.displayName = DialogTitle.displayName;

const EditDialogDescription = forwardRef<
  React.ElementRef<typeof DialogDescription>,
  React.ComponentPropsWithoutRef<typeof DialogDescription>
>(({ className, ...props }, ref) => (
  <DialogDescription
    ref={ref}
    className={cn("text-base text-left", className)}
    {...props}
  />
));

EditDialogDescription.displayName = DialogDescription.displayName;

function EditDialogFooter({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <DialogFooter
      className={cn(
        "border-t p-6 absolute bottom-0 left-0 right-0 bg-background z-50",
        className,
      )}
      {...props}
    />
  );
}

EditDialogFooter.displayName = DialogFooter.displayName;

export {
  EditDialog,
  EditDialogTrigger,
  EditDialogClose,
  EditDialogContent,
  EditDialogHeader,
  EditDialogFooter,
  EditDialogTitle,
  EditDialogDescription,
};
